import type { IStaticPageConfig } from '@modules/static-pages/types';
import { PermissionsList } from '@modules/permissions/constants';
import { Permission } from '@modules/permissions/utils';
import { EStaticPageName } from '@modules/static-pages/enums';

export const TRANSLATION_GROUP_STATIC_PAGES = Symbol('static-pages');

export const STATIC_PAGES_FAQ_ROUTE_NAME = Symbol('static-pages.faq');
export const STATIC_PAGES_TERMS_ROUTE_NAME = Symbol('static-pages.terms');
export const STATIC_PAGES_PRIVACY_ROUTE_NAME = Symbol('static-pages.privacy');
export const STATIC_PAGES_COMMISSION_ROUTE_NAME = Symbol('static-pages.commission');
export const STATIC_PAGES_HELP_ROUTE_NAME = Symbol('static-pages.help');
export const STATIC_PAGES_HELP_STATIC_PAGE_1_ROUTE_NAME = Symbol('static-pages.static-page-1');
export const STATIC_PAGES_HELP_STATIC_PAGE_2_ROUTE_NAME = Symbol('static-pages.static-page-2');
export const STATIC_PAGES_HELP_STATIC_PAGE_3_ROUTE_NAME = Symbol('static-pages.static-page-3');
export const STATIC_PAGES_HELP_STATIC_PAGE_4_ROUTE_NAME = Symbol('static-pages.static-page-4');
export const STATIC_PAGES_HELP_STATIC_PAGE_5_ROUTE_NAME = Symbol('static-pages.static-page-5');

export const STATIC_PAGES_GET_URL = ':namespace/static_pages/:page';
export const STATIC_PAGES_GET_QUERY_KEY = 'static-pages.get';

export const STATIC_PAGES_OPTIONS = Object.values(EStaticPageName).reduce((acc, page) => {
  acc[page] = {
    path: page,
    translationShortcodeSidebar: `AFFILIATE_PARTNERSHIP_${page}`,
    translationShortcodeSettings: `PAGE_${page}`,
  };
  return acc;
}, {} as Record<EStaticPageName, Pick<IStaticPageConfig, 'path' | 'translationShortcodeSidebar' | 'translationShortcodeSettings'>>);

export const STATIC_PAGES_CONFIG: Record<EStaticPageName, IStaticPageConfig> = {
  [EStaticPageName.FAQ]: {
    routeName: STATIC_PAGES_FAQ_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.FAQ].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_FAQ,
      PermissionsList.affiliate.STATICPAGES_FAQ,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.FAQ].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.FAQ].translationShortcodeSettings,
  },
  [EStaticPageName.TERMS]: {
    routeName: STATIC_PAGES_TERMS_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.TERMS].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_TERMS,
      PermissionsList.affiliate.STATICPAGES_TERMS,
      PermissionsList.anonymous.STATICPAGES_TERMS,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.TERMS].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.TERMS].translationShortcodeSettings,
  },
  [EStaticPageName.PRIVACY]: {
    routeName: STATIC_PAGES_PRIVACY_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.PRIVACY].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_PRIVACY,
      PermissionsList.affiliate.STATICPAGES_PRIVACY,
      PermissionsList.anonymous.STATICPAGES_PRIVACY,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.PRIVACY].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.PRIVACY].translationShortcodeSettings,
  },
  [EStaticPageName.COMMISSION]: {
    routeName: STATIC_PAGES_COMMISSION_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.COMMISSION].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_COMMISSION,
      PermissionsList.affiliate.STATICPAGES_COMMISSION,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.COMMISSION].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.COMMISSION].translationShortcodeSettings,
  },
  [EStaticPageName.HELP]: {
    routeName: STATIC_PAGES_HELP_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.HELP].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_HELP,
      PermissionsList.affiliate.STATICPAGES_HELP,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.HELP].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.HELP].translationShortcodeSettings,
  },
  [EStaticPageName.STATIC_PAGE_1]: {
    routeName: STATIC_PAGES_HELP_STATIC_PAGE_1_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_1].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_STATIC_PAGE_1,
      PermissionsList.affiliate.STATICPAGES_STATIC_PAGE_1,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_1].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_1].translationShortcodeSettings,
  },
  [EStaticPageName.STATIC_PAGE_2]: {
    routeName: STATIC_PAGES_HELP_STATIC_PAGE_2_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_2].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_STATIC_PAGE_2,
      PermissionsList.affiliate.STATICPAGES_STATIC_PAGE_2,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_2].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_2].translationShortcodeSettings,
  },
  [EStaticPageName.STATIC_PAGE_3]: {
    routeName: STATIC_PAGES_HELP_STATIC_PAGE_3_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_3].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_STATIC_PAGE_3,
      PermissionsList.affiliate.STATICPAGES_STATIC_PAGE_3,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_3].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_3].translationShortcodeSettings,
  },
  [EStaticPageName.STATIC_PAGE_4]: {
    routeName: STATIC_PAGES_HELP_STATIC_PAGE_4_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_4].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_STATIC_PAGE_4,
      PermissionsList.affiliate.STATICPAGES_STATIC_PAGE_4,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_4].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_4].translationShortcodeSettings,
  },
  [EStaticPageName.STATIC_PAGE_5]: {
    routeName: STATIC_PAGES_HELP_STATIC_PAGE_5_ROUTE_NAME,
    path: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_5].path,
    permission: new Permission(
      PermissionsList.admin.STATICPAGES_STATIC_PAGE_5,
      PermissionsList.affiliate.STATICPAGES_STATIC_PAGE_5,
    ),
    translationShortcodeSidebar: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_5].translationShortcodeSidebar,
    translationShortcodeSettings: STATIC_PAGES_OPTIONS[EStaticPageName.STATIC_PAGE_5].translationShortcodeSettings,
  },
};
